/**
 * O estado da mesa eletrônica (E74.F3, E78.F4): a carga do caso e dos documentos e as duas
 * mutações por CCB, com o guard de mutação em voo. Saiu da página na E162.F2.T02, quando a mesa
 * passou a desenhar a conclusão da CCB no alto e o detalhe por documento por expansão.
 */
import { useCallback, useEffect, useState } from 'react';

import type { DecisaoAcao } from '@/components/ExameEletronico/DecisaoDoPerito';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { getCase, type CaseRecordData } from '@/lib/cases';
import {
  concluirExame,
  ExameEletronicoError,
  listExamesEletronicos,
  reabrirExame,
  type ConclusaoEletronicaInput,
  type DivergenciaMantidaData,
  type ExameEletronicoCcbData,
  type ExameEletronicoErrorReason,
  type LeituraPendenteData,
  type ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico';
import { exigeConclusao, pecaConcluida } from '@/lib/exame_eletronico_ccb';

const t = ptBR.mesaEletronica;
const te = ptBR.exameEletronico;
const ti = ptBR.exameEletronico.importarProtocolo;

const _ERROR_MESSAGE: Record<ExameEletronicoErrorReason, string> = {
  case_not_found: te.errorCaseNotFound,
  document_not_found: te.errorDocumentNotFound,
  not_found: te.errorNotFound,
  modalidade_invalida: te.errorModalidadeInvalida,
  sem_protocolo: te.errorSemProtocolo,
  nao_verificado: te.errorNaoVerificado,
  justificativa_requerida: te.errorJustificativaRequerida,
  concluido: te.errorConcluido,
  consulta_pendente: te.errorConsultaPendente,
  nao_concluido: te.errorNaoConcluido,
  certificacao_requerida: te.errorCertificacaoRequerida,
  protocolo_peca_concluida: te.errorProtocoloPecaConcluida,
  custodia_interrompida: te.errorCustodiaInterrompida,
  aguardando_arquivos: te.errorAguardandoArquivos,
  protocolo_nao_importado: ti.erros.protocolo_nao_importado,
  documento_ilegivel: ti.erros.documento_ilegivel,
  documento_sem_texto: ti.erros.documento_sem_texto,
  arquivo_nao_suportado: ti.erros.arquivo_nao_suportado,
  arquivo_vazio: ti.erros.arquivo_vazio,
  arquivo_muito_grande: ti.erros.arquivo_muito_grande,
  // A mesa não anexa foto nem confere rosto; as razões existem no contrato da
  // raia e o mapa é exaustivo, então elas caem na frase genérica em vez de
  // sumir do tipo.
  arquivo_nao_e_imagem: te.errorGeneric,
  foto_integridade: te.errorGeneric,
  foto_indisponivel: te.errorGeneric,
  conferencia_sem_par: te.errorGeneric,
  conferencia_em_andamento: te.conferenciaFacial.erros.conferencia_em_andamento,
  conferencia_indisponivel: te.errorGeneric,
  provider_auth: te.errorGeneric,
  provider_credit: te.errorGeneric,
  sem_chave: te.errorGeneric,
  // E87.F2: a mesa não propõe candidatas; a chave existe porque o
  // classificador de erro da raia eletrônica é um só.
  candidata_nao_encontrada: te.errorGeneric,
  candidata_ja_confirmada: te.errorGeneric,
  validation: te.errorValidation,
  forbidden: te.errorForbidden,
  network: te.errorNetwork,
  unknown: te.errorGeneric,
};

function messageFor(err: unknown): string {
  if (err instanceof ExameEletronicoError) return _ERROR_MESSAGE[err.reason];
  return te.errorGeneric;
}

type PorCcb<T> = Readonly<Record<string, T>>;

function semChave<T>(mapa: PorCcb<T>, chave: string): PorCcb<T> {
  const { [chave]: descartado, ...resto } = mapa;
  void descartado;
  return resto;
}

/**
 * Estado da mesa: carga do caso + CCBs e as duas mutações por CCB.
 *
 * Extraído da página pelo mesmo motivo de `useWorkspaceData`: a página fica
 * sendo layout, e a regra de "quem está em voo, o que deu erro, o que deu
 * certo" — que é o guard de mutação — tem um lugar só.
 */
export function useMesaEletronica(caseId: string) {
  const client = useApiClient();
  const [caso, setCaso] = useState<CaseRecordData | null>(null);
  const [protocolo, setProtocolo] = useState<ProtocoloAssinaturaCasoData | null>(null);
  const [ccbs, setCcbs] = useState<ExameEletronicoCcbData[] | null>(null);
  // E118.F1 (GUS-127): as leituras propostas pendentes, avisadas junto à emissão.
  const [leiturasPendentes, setLeiturasPendentes] = useState<LeituraPendenteData[]>([]);
  // E119.F2 (GUS-132): o banco do caso; `null` = sem banco, avisado junto à emissão.
  const [bancoDoCaso, setBancoDoCaso] = useState<string | null | undefined>(undefined);
  // E177.F5.C01 (GUS-253 item 4): a divergência que quem enviou manteve na abertura.
  const [divergenciaMantida, setDivergenciaMantida] = useState<DivergenciaMantidaData | null>(null);
  const [erroGlobal, setErroGlobal] = useState<string | null>(null);
  const [acaoPorCcb, setAcaoPorCcb] = useState<PorCcb<DecisaoAcao>>({});
  const [erroPorCcb, setErroPorCcb] = useState<PorCcb<string>>({});
  const [sucessoPorCcb, setSucessoPorCcb] = useState<PorCcb<string>>({});

  const carregar = useCallback(async (): Promise<void> => {
    const [registro, lista] = await Promise.all([
      getCase(client, caseId),
      listExamesEletronicos(client, caseId),
    ]);
    setCaso(registro);
    setProtocolo(lista.protocolo);
    setCcbs(lista.pecas);
    setLeiturasPendentes(lista.leituras_pendentes ?? []);
    setBancoDoCaso(lista.banco_do_caso);
    setDivergenciaMantida(lista.divergencia_mantida ?? null);
  }, [client, caseId]);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const [registro, lista] = await Promise.all([
          getCase(client, caseId),
          listExamesEletronicos(client, caseId),
        ]);
        if (cancelado) return;
        setCaso(registro);
        setProtocolo(lista.protocolo);
        setCcbs(lista.pecas);
        setLeiturasPendentes(lista.leituras_pendentes ?? []);
        setBancoDoCaso(lista.banco_do_caso);
        setDivergenciaMantida(lista.divergencia_mantida ?? null);
      } catch (err) {
        if (!cancelado) setErroGlobal(messageFor(err));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId]);

  // E201.F2 (GUS-47 com a GUS-220): "Tentar de novo" depois da falha de carga. Relê e, só se der
  // certo, tira o aviso; se falhar de novo, o aviso diz a falha nova.
  const [tentando, setTentando] = useState(false);
  const tentarDeNovo = useCallback(async (): Promise<void> => {
    setTentando(true);
    try {
      await carregar();
      setErroGlobal(null);
    } catch (err) {
      setErroGlobal(messageFor(err));
    } finally {
      setTentando(false);
    }
  }, [carregar]);

  const executar = useCallback(
    async (
      docId: string,
      acao: Exclude<DecisaoAcao, null>,
      operacao: () => Promise<unknown>,
      sucesso: string,
    ): Promise<void> => {
      setAcaoPorCcb((atual) => ({ ...atual, [docId]: acao }));
      setErroPorCcb((atual) => semChave(atual, docId));
      setSucessoPorCcb((atual) => semChave(atual, docId));
      try {
        await operacao();
        // Relê a lista inteira em vez de costurar a resposta no estado local: a
        // conclusão da última CCB muda o STATUS DO CASO, e o stepper leria um
        // valor velho se só a linha da CCB fosse atualizada.
        await carregar();
        setSucessoPorCcb((atual) => ({ ...atual, [docId]: sucesso }));
      } catch (err) {
        setErroPorCcb((atual) => ({ ...atual, [docId]: messageFor(err) }));
      } finally {
        setAcaoPorCcb((atual) => semChave(atual, docId));
      }
    },
    [carregar],
  );

  const concluir = useCallback(
    (docId: string, conclusao: ConclusaoEletronicaInput): void => {
      void executar(
        docId,
        'concluindo',
        () => concluirExame(client, caseId, docId, conclusao),
        t.concluida,
      );
    },
    [client, caseId, executar],
  );

  const reabrir = useCallback(
    (docId: string, motivo: string): void => {
      void executar(
        docId,
        'reabrindo',
        () => reabrirExame(client, caseId, docId, motivo),
        t.reaberta,
      );
    },
    [client, caseId, executar],
  );

  // E162.F2.T02: só a CCB pede conclusão; o apoio verificado já está concluído.
  const aConcluir = (ccbs ?? []).filter(exigeConclusao);
  const total = aConcluir.length;
  const todasConcluidas = total > 0 && aConcluir.every(pecaConcluida);

  return {
    caso,
    protocolo,
    ccbs,
    leiturasPendentes,
    bancoDoCaso,
    divergenciaMantida,
    total,
    todasConcluidas,
    erroGlobal,
    acaoPorCcb,
    erroPorCcb,
    sucessoPorCcb,
    concluir,
    reabrir,
    recarregar: carregar,
    tentarDeNovo,
    tentando,
  };
}
